import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number using `toLocaleString()` while tolerating
 * undefined/null/NaN gracefully.
 *
 * Background (2.1.0-rc8 frontend hardening):
 * Studio's TranscodersPage crashed with
 *   "can't access property 'toLocaleString', t.segments is undefined"
 * whenever an edge was stopped, degraded, or returned partial rc7
 * status JSON. The crash trumped the rest of the page, so an
 * operator dealing with an outage couldn't even read the rows of
 * the still-healthy edges.
 *
 * Rules:
 *   - undefined / null   → fallback (default "—")
 *   - non-finite NaN     → fallback
 *   - explicit 0         → "0" (never the fallback — operator must
 *                          see the difference between "API said 0"
 *                          and "API didn't say")
 *   - any finite number  → `Number(value).toLocaleString()`
 *
 * @param {*} value     candidate value (any type)
 * @param {string} fallback  rendered when value is missing
 * @returns {string}
 */
export function formatNumber(value, fallback = "—") {
  if (value === undefined || value === null) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return n.toLocaleString();
}

/**
 * Format a byte count as a human-readable string (B/KB/MB/GB/TB).
 * Tolerates undefined/null/NaN.
 *
 * Used by the v2.1.1-rc10 maintenance daemon cards (Hot → Warm,
 * Warm → Cold, DVR Cleanup) which surface ``bytes_removed_from_ram``,
 * ``bytes_removed_from_warm``, and ``bytes_deleted`` counters.
 *
 * @param {*} value bytes (any type)
 * @param {string} fallback rendered when value is missing
 * @returns {string}
 */
export function formatBytes(value, fallback = "—") {
  if (value === undefined || value === null) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[i]}`;
}


/**
 * Resolve a split-daemon's freshness + age from the rc10 heartbeat
 * passthrough block at ``h.worker_status.external.external_daemons_health``.
 *
 * Falls back gracefully to the rc6 default thresholds when an
 * older edge doesn't include the block.
 *
 * @param {object} h heartbeat
 * @param {string} name one of: health | warm | cleanup | hot | playlist
 * @returns {{seen: boolean, fresh: boolean|null, ageSec: number|null,
 *            thresholdSec: number, skippedCount: number,
 *            tone: "healthy"|"drift"|"stalled"|"pending"}}
 */
export function resolveDaemonHealth(h, name) {
  const defaults = {
    health: 120, warm: 120, cleanup: 1200, hot: 16, playlist: 40,
  };
  const fallbackThreshold = defaults[name] || 120;
  const empty = {
    seen: false, fresh: null, ageSec: null,
    thresholdSec: fallbackThreshold, skippedCount: 0,
    tone: "pending",
  };
  const ws = (h && h.worker_status) || {};
  const ext = ws.external || {};
  const edh = ext.external_daemons_health || {};
  const entry = edh[name];
  if (!entry || typeof entry !== "object") return empty;
  const ageSec = Number.isFinite(Number(entry.age_sec))
    ? Number(entry.age_sec) : null;
  const thresholdSec = Number.isFinite(Number(entry.threshold_sec))
    ? Number(entry.threshold_sec) : fallbackThreshold;
  const fresh = typeof entry.fresh === "boolean" ? entry.fresh : null;
  const seen = !!entry.seen;
  const skippedCount = Number.isFinite(Number(entry.skipped_count))
    ? Number(entry.skipped_count) : 0;
  let tone = "pending";
  if (!seen) tone = "pending";
  else if (fresh === true) tone = "healthy";
  else if (fresh === false && ageSec !== null && ageSec <= thresholdSec * 3) {
    tone = "drift";
  } else if (fresh === false) tone = "stalled";
  return { seen, fresh, ageSec, thresholdSec, skippedCount, tone };
}


/**
 * Resolve a split-worker's own sidecar detail block (warm_cascade,
 * playlist_sanitize, health_snapshot) from the rc10 heartbeat
 * passthrough. Returns ``null`` when the edge hasn't published the
 * block yet (older Edge → "Data pending" fallback in the UI).
 *
 * @param {object} h heartbeat
 * @param {string} name worker key on ext (e.g. "warm_cascade",
 *   "playlist_sanitize", "health_snapshot", "hot_cascade",
 *   "dvr_cleanup")
 * @returns {object|null}
 */
export function resolveDaemonDetail(h, name) {
  const ws = (h && h.worker_status) || {};
  const ext = ws.external || {};
  const block = ext[name];
  if (!block || typeof block !== "object"
      || Object.keys(block).length === 0) {
    return null;
  }
  return block;
}


/**
 * Resolve the per-edge cascade view-model from a heartbeat.
 *
 * Two heartbeat shapes flow through Studio:
 *
 *   (A) Legacy edges (e.g. TRN-Media01 on inline maintenance)
 *       carry `h.storage_tiers.cascade` with `last_run`,
 *       `last_moved_hot_to_warm`, `last_moved_warm_to_cold`,
 *       `total_moved`, `last_errors`. The inline cascade thread
 *       owns these counters.
 *
 *   (B) rc9 external-daemon edges (e.g. trn-cnd02) do NOT run
 *       the inline cascade thread, so the legacy `cascade` block
 *       is absent. Instead the edge runtime's rc7 overlay puts
 *       the rc9 cascade telemetry under
 *       `h.worker_status.external.hot_cascade` (= status JSON's
 *       `hot_cascade_detail` block) plus `last_run_ts`.
 *
 * Without this helper, Studio's Edge tab read only path (A) and
 * rendered the "Last cascade / N to SSD / N to NFS / N total moved"
 * strip as "Not yet run" on every rc9 external edge, even when
 * the daemon was clearly moving real bytes (trn-cnd02 reported
 * 3.47 GB → 24.21 GB cascade activity but the Edge tab still
 * showed dashes).
 *
 * Fallback contract per operator's rc9 follow-on:
 *
 *   moved_to_ssd        = legacy.last_moved_hot_to_warm
 *                       ?? hot_cascade.removed_from_ram
 *                       ?? hot_cascade.copied_or_verified
 *   moved_bytes_to_ssd  = hot_cascade.bytes_removed_from_ram  (external only)
 *   moved_to_nfs        = legacy.last_moved_warm_to_cold  ?? null
 *                         (rc9 external doesn't measure NFS yet)
 *   total_moved         = legacy.total_moved
 *                       ?? hot_cascade.removed_from_ram
 *                       ?? hot_cascade.copied_or_verified
 *   last_run            = legacy.last_run  ??  external.last_run_ts
 *   errors              = legacy.last_errors  ??  hot_cascade.errors
 *
 * @param {object} h  heartbeat (typed loosely — any field may be missing)
 * @returns {object}  view-model with:
 *   - source: "legacy" | "external" | "none"
 *   - lastRun: epoch seconds | null
 *   - movedToSsd: number | null
 *   - movedBytesToSsd: number | null   (external path only)
 *   - movedToNfs: number | null
 *   - totalMoved: number | null
 *   - errors: number   (defaults 0)
 */
export function resolveCascadeView(h) {
  // Defensive everywhere — Studio renders this for every edge in
  // the list, including disconnected ones with `health: undefined`.
  const legacy = h && h.storage_tiers && h.storage_tiers.cascade;
  const external = h && h.worker_status && h.worker_status.external;
  const ext_hc = external && external.hot_cascade;

  // Heuristic for "legacy is actually populated": last_run > 0 OR
  // ANY of the counters is > 0. A legacy block with all zeros + 0
  // last_run is functionally indistinguishable from "never ran",
  // so we prefer the external block when it has data.
  const legacyHas = !!legacy && (
    (legacy.last_run > 0) ||
    (legacy.last_moved_hot_to_warm > 0) ||
    (legacy.last_moved_warm_to_cold > 0) ||
    (legacy.total_moved > 0)
  );
  const externalHas = !!ext_hc && (
    (ext_hc.removed_from_ram > 0) ||
    (ext_hc.copied_or_verified > 0) ||
    (ext_hc.bytes_removed_from_ram > 0) ||
    (external && external.last_run_ts > 0)
  );

  if (legacyHas) {
    return {
      source: "legacy",
      lastRun: legacy.last_run || null,
      movedToSsd: typeof legacy.last_moved_hot_to_warm === "number"
        ? legacy.last_moved_hot_to_warm : null,
      movedBytesToSsd: null,
      movedToNfs: typeof legacy.last_moved_warm_to_cold === "number"
        ? legacy.last_moved_warm_to_cold : null,
      totalMoved: typeof legacy.total_moved === "number"
        ? legacy.total_moved : null,
      errors: legacy.last_errors || 0,
    };
  }
  if (externalHas) {
    const removed = (typeof ext_hc.removed_from_ram === "number"
      && ext_hc.removed_from_ram > 0)
      ? ext_hc.removed_from_ram
      : (typeof ext_hc.copied_or_verified === "number"
          ? ext_hc.copied_or_verified : null);
    return {
      source: "external",
      lastRun: (external && external.last_run_ts) || null,
      movedToSsd: removed,
      movedBytesToSsd: typeof ext_hc.bytes_removed_from_ram === "number"
        ? ext_hc.bytes_removed_from_ram : null,
      // rc9 external cascade doesn't measure warm→cold yet.
      // Render as null (→ "—" via formatNumber), NOT as 0
      // (which would imply "explicit zero from the API").
      movedToNfs: null,
      // For rc9 external the cascade detail counts only what
      // happened THIS tick. We use the same value for "total
      // moved" so the strip still has a number — the operator's
      // request was explicitly "do not render dash when external
      // rc9 status has valid hot_cascade_detail".
      totalMoved: removed,
      errors: ext_hc.errors || 0,
    };
  }
  // Neither path has data — let the existing "Not yet run" UI fire.
  return {
    source: "none",
    lastRun: null,
    movedToSsd: null,
    movedBytesToSsd: null,
    movedToNfs: null,
    totalMoved: null,
    errors: 0,
  };
}

/**
 * Format a byte count as a compact GB string ("24.21 GB"), or
 * the formatNumber fallback when the input is missing/non-finite.
 *
 * @param {*} bytes
 * @param {string} fallback  default "—"
 */
export function formatBytesGB(bytes, fallback = "—") {
  if (bytes === undefined || bytes === null) return fallback;
  const n = Number(bytes);
  if (!Number.isFinite(n)) return fallback;
  return `${(n / (1024 ** 3)).toFixed(2)} GB`;
}

/**
 * Detect whether an edge's heartbeat carries enough evidence of
 * rc9 external-daemon activity that Studio should NOT render an
 * "empty / never recorded" state, even when the legacy
 * `dvr_streams` inventory is empty.
 *
 * Operator's rc9 follow-on: on trn-cnd02, the local edge status
 * JSON was healthy (`mode=external-daemon`, `ok=true`, hot/warm
 * bytes populated, daemon.iteration increasing, hot_cascade
 * moving real bytes) BUT Studio's DVR tab still showed
 * "No DVR recordings yet" because Studio only consulted
 * `dvr_streams`. This helper lets the DVR panel pick a richer
 * empty-state when the rc9 telemetry contradicts the
 * legacy-inventory's silence.
 *
 * @param {object} h  heartbeat object
 * @returns {object}  {
 *   active: boolean,
 *   mode: string ("external-daemon" | "external" | "inline" | "unknown"),
 *   reasons: string[]  // human-readable evidence list
 * }
 */
export function describeExternalDaemonActivity(h) {
  if (!h || typeof h !== "object") {
    return { active: false, mode: "unknown", reasons: [] };
  }
  const reasons = [];
  const ext = h.worker_status && h.worker_status.external;
  const tiers = h.storage_tiers || {};
  const hotUsed = Number(tiers.hot && tiers.hot.size_gb);
  const warmUsed = Number(tiers.warm && tiers.warm.size_gb);
  const hc = ext && ext.hot_cascade;

  let mode = "unknown";
  if (ext && ext.last_run_ts > 0) {
    // The rc7 overlay records the external maintenance run.
    // If the daemon block carries `iteration`, we're in
    // long-running daemon mode (rc8+). Otherwise it's a one-shot
    // `--once` invocation (rc7). Either way the source is the
    // external maintenance status JSON.
    if (ext.iteration > 0 || (ext.daemon && ext.daemon.iteration > 0)) {
      mode = "external-daemon";
    } else {
      mode = "external";
    }
  } else if (tiers.source === "external_maintenance_status") {
    mode = "external";
  } else if (tiers.cascade && tiers.cascade.last_run > 0) {
    mode = "inline";
  }

  if (ext && ext.last_run_ts > 0) {
    reasons.push("external maintenance ran");
  }
  if (Number.isFinite(hotUsed) && hotUsed > 0) {
    reasons.push(`hot tier ${hotUsed.toFixed(2)} GB`);
  }
  if (Number.isFinite(warmUsed) && warmUsed > 0) {
    reasons.push(`warm tier ${warmUsed.toFixed(2)} GB`);
  }
  if (hc && (hc.scanned > 0 || hc.copied_or_verified > 0
             || hc.removed_from_ram > 0)) {
    reasons.push("hot_cascade activity");
  }
  return {
    active: reasons.length > 0,
    mode,
    reasons,
  };
}

/**
 * Inspect a heartbeat for the four segment-integrity counters
 * (`segments_received` / `_validated` / `_rejected` / `reject_rate`).
 *
 * Returns `{available, values}` where:
 *   - `available` is true iff at least one of the four counters is a
 *     finite POSITIVE number — OR the edge explicitly advertises
 *     `segment_integrity_instrumented: true`. Pre-2.1.0-rc14 we
 *     considered explicit `0`s as evidence of an answered API; that
 *     produced false "0/0/0/0" cards on edges that don't instrument
 *     the counters yet (trn-cnd02 field bug: edge clearly ingesting
 *     segments via cascade + dvr_cleanup, integrity card stuck at
 *     0/0/0/0). The four counters are STRICTLY MONOTONIC LIFETIME
 *     totals on instrumented edges, so "all zeros forever" is the
 *     unambiguous signature of "feature not wired" on a live edge.
 *   - `values` is the per-field finite number OR `null` when
 *     missing/NaN. Callers render via `formatNumber` so `null`
 *     becomes the "—" fallback automatically.
 *
 * @param {object} h heartbeat
 * @returns {{available: boolean, values: {received, validated, rejected, rejectRate}}}
 */
export function getSegmentIntegrity(h) {
  const out = { received: null, validated: null, rejected: null,
                rejectRate: null };
  if (!h || typeof h !== "object") {
    return { available: false, values: out };
  }
  const fields = [
    ["received", "segments_received"],
    ["validated", "segments_validated"],
    ["rejected", "segments_rejected"],
    ["rejectRate", "reject_rate"],
  ];
  let anyPositive = false;
  for (const [k, src] of fields) {
    const raw = h[src];
    if (raw === undefined || raw === null) continue;
    const n = Number(raw);
    if (Number.isFinite(n)) {
      out[k] = n;
      if (n > 0) anyPositive = true;
    }
  }
  // Explicit operator opt-in — an edge that's wired the counters but
  // has genuinely seen 0 segments since boot can set this flag to
  // force the populated view (otherwise it'd be hidden as
  // "not instrumented"). Lifetime counters never reset back to 0
  // on a live edge, so absence of this flag + all-zeros = not wired.
  const explicit = h.segment_integrity_instrumented === true;
  return { available: anyPositive || explicit, values: out };
}


/**
 * Resolve the DVR cleanup retention counters from a heartbeat,
 * preferring the legacy inline `total_sweeps`/`total_removed` when
 * non-zero and falling back to the rc13.4 external
 * `worker_status.external.dvr_cleanup` block. Mirrors
 * `resolveCascadeView`'s legacy-then-external priority so the worker
 * tile shows real numbers on external-daemon edges (trn-cnd02 field
 * symptom: tile rendered "0 sweeps · 0 removed" even though
 * `dvr_cleanup_detail.deleted_count` on the edge was non-zero).
 *
 * @param {object} h heartbeat
 * @returns {{source: string, lastRun: number|null,
 *            totalSweeps: number|null, totalRemoved: number|null,
 *            inflight: boolean,
 *            lastBytesDeleted: number|null,
 *            lastChannelsScanned: number|null}}
 */
export function resolveDvrCleanupView(h) {
  const ws = (h && h.worker_status) || {};
  const inline = ws.dvr_cleanup || {};
  const ext = ws.external || {};
  const extDvc = ext.dvr_cleanup || {};

  const inlineHas =
    (Number(inline.total_sweeps) || 0) > 0
    || (Number(inline.total_removed) || 0) > 0
    || (Number(inline.last_run_ts) || 0) > 0;
  const externalHas =
    (Number(extDvc.deleted_count) || 0) > 0
    || (Number(extDvc.channels_scanned) || 0) > 0
    || (Number(ext.last_run_ts) || 0) > 0;

  if (inlineHas) {
    return {
      source: "legacy",
      lastRun: Number(inline.last_run_ts) || null,
      totalSweeps: Number.isFinite(Number(inline.total_sweeps))
        ? Number(inline.total_sweeps) : null,
      totalRemoved: Number.isFinite(Number(inline.total_removed))
        ? Number(inline.total_removed) : null,
      inflight: !!inline.inflight,
      lastBytesDeleted: Number.isFinite(Number(inline.last_bytes_deleted))
        ? Number(inline.last_bytes_deleted) : null,
      lastChannelsScanned:
        Number.isFinite(Number(inline.last_channels_scanned))
          ? Number(inline.last_channels_scanned) : null,
    };
  }
  if (externalHas) {
    return {
      source: "external",
      lastRun: Number(ext.last_run_ts) || null,
      // rc13.4 external block reports per-tick `deleted_count`. We
      // surface it as the headline counter — Studio's tile labels it
      // "removed" which is operator-equivalent to "deleted" here.
      totalSweeps: null,
      totalRemoved: Number.isFinite(Number(extDvc.deleted_count))
        ? Number(extDvc.deleted_count) : null,
      inflight: !!extDvc.in_progress,
      lastBytesDeleted: Number.isFinite(Number(extDvc.bytes_deleted))
        ? Number(extDvc.bytes_deleted) : null,
      lastChannelsScanned: Number.isFinite(Number(extDvc.channels_scanned))
        ? Number(extDvc.channels_scanned) : null,
    };
  }
  return {
    source: "none",
    lastRun: null,
    totalSweeps: null,
    totalRemoved: null,
    inflight: false,
    lastBytesDeleted: null,
    lastChannelsScanned: null,
  };
}
